import { createPatchData } from 'features/fde/helpers/rolodex/createPatchData'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { ticketDashboardSettingsSchema } from 'features/libra/dashboard/ticketDashboard/libra/settings/dashboardSettings'
import { RolodexEntryQueryBody, useRolodexEntries } from 'features/rolodex'
import isEmpty from 'lodash/isEmpty'
import { useCallback, useMemo } from 'react'
import { GeckoEntry, RolodexEntryTagsType, RolodexEntryType } from 'types'
import z from 'zod'

const libraDashboardConfigSchema = z
  .object({
    title: z.string().readonly().default('Untitled Dashboard'),
    roiVisibleUserIds: z.array(z.string()).readonly().optional(),
    /** Present (even `{}`) switches the dashboard to the ticket dashboard. */
    ticketDashboard: ticketDashboardSettingsSchema.optional(),
  })
  .readonly()

export type LibraDashboardConfig = z.infer<
  typeof libraDashboardConfigSchema
> & {
  id: string
  tags: RolodexEntryTagsType
}

const mapEntryToLibraDashboardConfig = (
  entry: GeckoEntry
): LibraDashboardConfig => {
  const parsed = libraDashboardConfigSchema.parse(entry.data)
  return {
    id: entry.id,
    tags: entry.tags,
    ...parsed,
  }
}

export function useDashboardConfigs({
  tags,
}: {
  tags?: RolodexEntryTagsType
}): {
  configs: ReadonlyArray<LibraDashboardConfig>
  isLoading: boolean
  hasEverLoaded: boolean
  forceRefresh: () => void
} {
  const queryBody = useMemo<RolodexEntryQueryBody | undefined>(() => {
    const hasTags = tags && !isEmpty(tags)
    if (!hasTags) {
      return undefined
    }

    return {
      type: RolodexEntryType.LIBRA_DASHBOARD_CONFIG,
      tags,
    }
  }, [tags])

  const {
    entries: dashboardConfigs,
    isLoading,
    hasEverLoaded,
    forceRefresh,
  } = useRolodexEntries(queryBody)

  return useMemo(
    () => ({
      configs: (dashboardConfigs ?? []).map(mapEntryToLibraDashboardConfig),
      isLoading,
      hasEverLoaded,
      forceRefresh,
    }),
    [dashboardConfigs, forceRefresh, hasEverLoaded, isLoading]
  )
}

function dashboardConfigDataRecord(config: {
  title: string
  roiVisibleUserIds?: ReadonlyArray<string>
}): Record<string, unknown> {
  const data: Record<string, unknown> = { title: config.title }
  if (config.roiVisibleUserIds !== undefined) {
    data.roiVisibleUserIds = [...config.roiVisibleUserIds]
  }
  return data
}

export function useUpdateDashboardConfig(): (args: {
  config: LibraDashboardConfig
  roiVisibleUserIds: ReadonlyArray<string>
}) => Promise<void> {
  const { rolodexPatch } = useRolodexEdit()
  return useCallback(
    async ({ config, roiVisibleUserIds }) => {
      const patch = createPatchData(
        config.id,
        dashboardConfigDataRecord(config),
        dashboardConfigDataRecord({
          title: config.title,
          roiVisibleUserIds,
        })
      )
      if (patch) {
        await rolodexPatch([patch])
      }
    },
    [rolodexPatch]
  )
}
